import classNames from "classnames";
import Link from "next/link";
import * as React from "react";
import { Button } from "@components/Button";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import LazyBgImage from "@components/LazyBgImage";
import LazyImage from "@components/LazyImage";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import styles from "./index.module.css";

const About: React.FC = () => {
  return (
    <CommonLayout hasFooter>
      <CommonHead
        title={"スポーツ観戦の新しい楽しみ方 Fansta(ファンスタ)とは？"}
        description={
          "Fanstaは見たい試合を上映しているスポーツバーを見つけることができるサービスです。サッカーや野球の試合日程、好きなチーム、近くのエリアから検索・予約して、仲間と一緒にスポーツ観戦を楽しもう。"
        }
        ogImage={""}
        ogUrl={paths.about}
        canonicalUrl={paths.about}
      />

      <section className={styles.keyVisual}>
        <h1 className="md:none">
          <LazyImage
            className={styles.autoHeight}
            src={IFImg("/img/about/sp/keyVisual.png")}
            width={750}
            height={640}
            alt="スポーツ観戦の新しい楽しみ方 Fanstaとは？"
          />
        </h1>

        <h1 className="none md:block">
          <LazyImage
            className={styles.autoHeight}
            src={IFImg("/img/about/pc/keyVisual.png")}
            alt="スポーツ観戦の新しい楽しみ方 Fanstaとは？"
            width={1440}
            height={640}
          />
        </h1>
      </section>

      <section className={classNames(styles.section, styles.lead)}>
        <div className={styles.leadBg} />
        <div className="l-main__innerWidth">
          <div className={styles.sectionTitle}>
            <h2 className={styles.leadTitle}>
              <img
                className={styles.autoHeight}
                src={IFImg("/img/about/title_001.png")}
                width="354"
                alt="スポーツ観戦の新しい楽しみ方 Fanstaとは？"
              />
            </h2>
          </div>
        </div>
        <div className={styles.leadSub}>
          <h3 className={styles.sectionSub}>
            <span>お店で楽しむ</span>
            <br />
            <span>スポーツライブビューイング</span>
            <br />
            <span>サービス</span>
          </h3>
        </div>
        <div className="l-main__innerWidth">
          <div className={styles.leadBox}>
            <p>スポーツは、やっぱり仲間と観るとおもしろい！</p>
            <p>
              Fanstaは、ファンがつくるもうひとつのスタジアム。お店のライブビューイングで楽しむスポーツ
              × ソーシャルな新しい応援スタイルで最高の空間＆瞬間をシェアできる。
            </p>
            <p>さぁ、近くのスタジアム（お店）に集まろう。</p>
          </div>
        </div>
      </section>

      <section className={classNames(styles.section, styles.value1)}>
        <div className={styles.sectionImg}>
          <LazyImage
            className="md:none"
            src={IFImg("/img/about/sp/img_002.jpg")}
            width={748}
            height={480}
            alt=""
          />

          <LazyImage
            className="none md:block"
            src={IFImg("/img/about/pc/img_002.jpg")}
            width={960}
            height={400}
            alt=""
          />
        </div>
        <div className="l-main__innerWidth">
          <div className={classNames(styles.sectionTitle, styles.isRight)}>
            <h2 className={styles.value1Title}>
              <img
                src={IFImg("/img/about/title_002.png")}
                width="423"
                alt="スポーツ観戦ができるスポーツバーが見つかる"
              />
            </h2>
          </div>
          <p className={styles.sectionText}>
            見たい試合、好きなチーム、近くのエリアからスポーツ観戦できるスポーツバーがみつかります。
          </p>
          <p className={styles.sectionNote}>
            ※
            対象となる試合は、DAZNで配信されている明治安田Jリーグ、AFCチャンピオンズリーグ、プロ野球の一部に限ります。
          </p>
        </div>
      </section>

      <section className={classNames(styles.section, styles.value2)}>
        <div className={styles.sectionImg}>
          <LazyImage
            className="md:none"
            src={IFImg("/img/about/sp/img_003.jpg")}
            width={748}
            height={480}
            alt=""
          />

          <LazyImage
            className={classNames(styles.autoHeight, "none md:block")}
            src={IFImg("/img/about/pc/img_003.jpg")}
            width={958}
            height={398}
            alt=""
          />
        </div>
        <div className="l-main__innerWidth">
          <div className={classNames(styles.sectionTitle)}>
            <h2 className={styles.value2Title}>
              <img
                className={styles.autoHeight}
                src={IFImg("/img/about/title_003.png")}
                width="474"
                alt="予約してスポーツ観戦をスポーツバーで楽しむ"
              />
            </h2>
          </div>
          <p className={styles.sectionText}>
            お店を予約して、仲間とスポーツ観戦を楽しもう。
          </p>
        </div>
      </section>

      <section className={styles.fansta}>
        <LazyBgImage
          src={IFImg("/img/about/sp/img_004.png")}
          className={classNames(styles.fanstaBg)}
        />

        <div className={classNames("l-main__innerWidth relative")}>
          <p className={classNames(styles.fsTitle, styles.fanstaTitle)}>
            <img
              src="/img/logo_fansta.svg"
              width="180"
              alt="Fansta"
              className={styles.autoHeight}
            />
          </p>

          <div>
            <p className={styles.fsText}>
              試合日程、近くのエリア、好きなチームから
              <br />
              目的の試合を上映しているスポーツバーを
              <br className="md:none" />
              見つけてスポーツ観戦を楽しもう。
            </p>

            <Link href={paths.home} legacyBehavior>
              <Button
                className={styles.btn}
                styleType="outlined"
                colorType="primary"
              >
                Fanstaでスポーツバーを探す
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className={styles.app}>
        <div className={styles.appContainer}>
          <div className={styles.appBox}>
            <p className={styles.appTitle}>
              <span>アプリでも</span>
              <br />
              <span>かんたんネット予約</span>
            </p>
            <div className="md:none">
              <Button
                className={styles.appButton}
                styleType="outlined"
                colorType="primary"
                href={paths.appOneLinkCommon}
              >
                アプリを使ってみる
              </Button>
            </div>
            <div className="none md:block">
              <ul className={classNames(styles.appList)}>
                <li className={styles.ios}>
                  <Link href={paths.appStore} target="_blank">
                    <img
                      src={IFImg("/img/about/appstore.svg")}
                      width={164}
                      height={60}
                      alt=""
                    />
                  </Link>
                </li>
                <li className={styles.aos}>
                  <Link href={paths.googlePlay} target="_blank">
                    <img
                      src={IFImg("/img/about/googleplay.png")}
                      width={200}
                      height={60}
                      alt=""
                    />
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className={styles.appImage}>
            <span className="md:none">
              <img
                src={IFImg("/img/about/sp/app_image.png")}
                width={390}
                height={362}
                alt=""
              />
            </span>
            <span className="none md:block">
              <img
                src={IFImg("/img/about/pc/app_image.png")}
                width={892}
                height={730}
                alt=""
              />
            </span>
          </div>
        </div>
      </section>

      <section className={styles.shop}>
        <LazyBgImage
          src={IFImg("/img/about/sp/img_005.jpg")}
          className={classNames(styles.shopBg, "md:none")}
        />

        <LazyBgImage
          src={IFImg("/img/about/pc/img_005.jpg")}
          className={classNames(styles.shopBg, "none md:block")}
        />

        <div className="l-main__innerWidth relative">
          <p className={classNames(styles.fsTitle, styles.shopTitle)}>
            お店の方へ
          </p>
          <p className={styles.fsText}>
            掲載・資料請求をご希望の店舗様は
            <br />
            以下からお問い合わせください。
          </p>

          <Button
            className={styles.btn}
            styleType="outlined"
            colorType="primary"
            href={paths.aboutShop}
          >
            お店の方はこちら
          </Button>
        </div>
      </section>
    </CommonLayout>
  );
};

export default About;
